import { FC, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { twMerge } from 'tailwind-merge';

import { ArrowIcon, CloseIcon, FilterIcon, RefreshIcon } from '../../../assets/icons/index';
import { RefreshIconVariant } from '../../../assets/icons/refresh-icon';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import { Checkbox } from '../../../components/checkbox/index';
import { StaggerFadeIn, StaggerItem } from '../../../components/motion/index';
import { PaginationController } from '../../../components/pagination-controller/index';
import { Sheet, SheetContent } from '../../../components/sheet/index';
import {
  EventHooks,
  useFormat,
  useLocale,
  useModalState,
  useNavigate,
  usePagination,
} from '../../../hooks/index';
import {
  eventFormType,
  EventLifecycleStatus,
  EventTypeBackend,
  EventUnion,
  isPendingLifecycleStatus,
  SortByDate,
  sortByDateMap,
  SortByType,
  sortByTypeMap,
  sortByTypeTitle,
} from '../../../types/events.types';
import { PageRoute } from '../../../types/page.types';
import { toRound } from '../../../utils/get-rounded-number.util';
import { toNumber } from '../../../utils/to-number.util';
import { EventLifecycleWrapper } from '../components/event-lifecycle-wrapper';
import { EventSignatureStatusCell } from '../components/event-signature-status-cell';
import {
  EventHistoryRowPlaceholder,
  EventsHistorySectionPlaceholder,
} from '../components/events-loading-placeholders';
import { EmptyEventHistory } from '../empty-event-history';
import { Tag, TagVariant } from '../tag';
import { getEventTransactionDate } from '../utils';
import {
  getPoolAdjustmentTitle,
  isPoolAdjustmentEvent,
  isPoolEvent,
} from '../utils/pool-event-row.util';
import { DatePicker } from './date-picker';

const sortByTypeValues = Object.values(SortByType);

type EventsMobileHistoryProps = {
  companyId: string;
};

const itemsToFetch = 13;

export const EventsMobileHistory: FC<EventsMobileHistoryProps> = ({ companyId }) => {
  const [currentPage, setCurrentPage] = usePagination('events-mobile-history');
  const navigate = useNavigate(PageRoute.EVENTS);
  const { messagesLocale } = useLocale();
  const [selectedTypeFilters, setTypeFilters] = useState<SortByType[]>(sortByTypeValues);
  const [selectedFilter, setSelectedFilter] = useState<'ByDate' | 'ByType'>('ByType');
  const [selectedDateFilter, setDateFilter] = useState(SortByDate.ALL_TIME);
  const [selectedCustomDate, setCustomDate] = useState<DateRange>({
    from: undefined,
    to: undefined,
  });

  const [selectedFilters, setSelectedFilters] = useState<{
    customDate: DateRange | null;
    dateFilter: SortByDate;
    types: SortByType[];
  }>({ customDate: null, dateFilter: SortByDate.ALL_TIME, types: [] });

  const { events, totalPages, isEmpty, isSearchResultEmpty, isLoading } = EventHooks.useEvents({
    companyId,
    dateFilter: selectedFilters.dateFilter,
    typesFilter: selectedFilters.types,
    customDateFilter: selectedFilters.customDate || { from: undefined, to: undefined },
    currentPage: currentPage,
    take: itemsToFetch,
    // Timeline renders a pending create as a skeleton row, so it wants the client placeholder.
    includePendingCreates: true,
  });

  const { format } = useFormat();
  const { isOpen, toggler, handleOpenModal, handleCloseModal, handleSuccessModal } =
    useModalState();

  const handleResetFilters = () => {
    setSelectedFilters({
      customDate: null,
      dateFilter: SortByDate.ALL_TIME,
      types: sortByTypeValues,
    });
    setTypeFilters(sortByTypeValues);
    setDateFilter(SortByDate.ALL_TIME);
  };

  const handleApplyFilters = () => {
    setSelectedFilters({
      customDate: selectedCustomDate,
      dateFilter: selectedDateFilter,
      types: selectedTypeFilters,
    });
    handleSuccessModal();
  };
  const isSelectedAllTypeFilters = selectedTypeFilters.length === sortByTypeValues.length;

  return (
    <>
      <div className="flex w-full flex-col gap-4 rounded-md bg-white p-3 shadow-sm">
        <div className="flex w-full justify-between border-b border-gray-100 pb-4">
          <div className="flex items-center gap-1">
            <RefreshIcon variant={RefreshIconVariant.EVENTS} />
            <span className="text-base font-semibold text-gray-700">
              <AppFormattedMessage id="EVENT_HISTORY" />
            </span>
          </div>
          <Button
            className="centered flex !size-9 h-fit w-fit rounded border border-gray-100 bg-white"
            onClick={handleOpenModal}
            styleType="NONE"
          >
            <FilterIcon />
          </Button>
        </div>
        {isLoading ? (
          <EventsHistorySectionPlaceholder isMobile rows={5} />
        ) : !isEmpty ? (
          <StaggerFadeIn className="flex w-full flex-col gap-4" key={currentPage}>
            {events.map((event) => {
              const { type } = event;

              if (isPendingLifecycleStatus(event.lifecycleStatus)) {
                return (
                  <StaggerItem key={event.eventId}>
                    <EventLifecycleWrapper event={event}>
                      <EventHistoryRowPlaceholder />
                    </EventLifecycleWrapper>
                  </StaggerItem>
                );
              }

              const mergedEventUnion = event as MergeUnion<EventUnion>;

              const transactions = mergedEventUnion.items || mergedEventUnion.exercise?.items || [];
              const transactionDate = getEventTransactionDate(event);
              const exerciseTransactions =
                type === EventTypeBackend.EXERCISE ||
                type === EventTypeBackend.APPROVAL ||
                type === EventTypeBackend.PAYOUT ||
                type === EventTypeBackend.EXPIRE
                  ? (mergedEventUnion.exercise?.items?.length ?? 0)
                  : 0;
              const transactionCount =
                type === EventTypeBackend.VALUATION ||
                type === EventTypeBackend.NEW_PLAN ||
                isPoolEvent(event)
                  ? 1
                  : exerciseTransactions || transactions.length;

              const originalName = mergedEventUnion.valuation?.name || mergedEventUnion.name;
              const name = isPoolAdjustmentEvent(event)
                ? getPoolAdjustmentTitle(event, messagesLocale[eventFormType[type]])
                : originalName || <AppFormattedMessage id={eventFormType[type]} />;

              const shouldRenderPrePostValuation =
                type === EventTypeBackend.FUNDRAISING_ROUND ||
                type === EventTypeBackend.SHARE_ISSUANCE;

              const postMoneyValuation = toNumber(
                toRound(
                  (mergedEventUnion.valuation?.sharePrice || 0) *
                    (mergedEventUnion.valuation?.issuedShares || 0),
                  2,
                ),
              );

              const preValuation = toNumber(
                toRound(
                  (mergedEventUnion.valuation?.sharePrice || 0) *
                    (mergedEventUnion.valuation?.issuedSharesOnStart || 0),
                  2,
                ),
              );

              const shouldRenderMobileEventId =
                type === EventTypeBackend.SAFE_CONVERSION ||
                type === EventTypeBackend.BUYBACK ||
                type === EventTypeBackend.GRANT ||
                type === EventTypeBackend.CONVERSION ||
                type === EventTypeBackend.SECONDARIES ||
                type === EventTypeBackend.SHARE_ISSUANCE ||
                type === EventTypeBackend.EXERCISE ||
                type === EventTypeBackend.APPROVAL ||
                type === EventTypeBackend.PAYOUT ||
                type === EventTypeBackend.EXPIRE;

              const mobileEventDisplayId = (
                type === EventTypeBackend.GRANT ? event.id : event.eventId
              )
                .split('-')[0]
                .toUpperCase();

              const isFailedMutation = event.lifecycleStatus === EventLifecycleStatus.FAILED;

              return (
                <StaggerItem key={event.eventId}>
                  <div
                    className={twMerge(
                      'flex w-full flex-col divide-y divide-gray-100 overflow-hidden rounded-md pt-3 shadow-sm',
                      isFailedMutation && 'ring-red-300 ring-1',
                    )}
                  >
                    <div className="flex w-full items-center justify-between gap-1 px-3 pb-3">
                      <div className="flex max-w-[calc(100%-100px)] flex-grow flex-col">
                        <span className="truncate text-base font-semibold text-gray-700">
                          {name}
                        </span>
                        <span className="flex items-center gap-2 text-label-sm font-medium text-gray-500">
                          {event.type === EventTypeBackend.NEW_PLAN && (
                            <>
                              <span>{event.sharePlan.name}</span>
                              <span className="size-[3px] shrink-0 rounded-full bg-gray-300" />
                            </>
                          )}
                          {event.type === EventTypeBackend.NEW_POOL && (
                            <>
                              <span>{event.pool.name}</span>
                              <span className="size-[3px] shrink-0 rounded-full bg-gray-300" />
                            </>
                          )}
                          <span>{format(transactionDate, 'dd/MM/yyyy')}</span>
                          <span className="size-[3px] shrink-0 rounded-full bg-gray-300" />
                          <span>
                            {transactionCount}{' '}
                            {transactionCount > 1 ? (
                              <AppFormattedMessage id="TRANSACTIONS" />
                            ) : (
                              <AppFormattedMessage id="TRANSACTION" />
                            )}
                          </span>
                          {shouldRenderMobileEventId && (
                            <>
                              <span className="size-[3px] shrink-0 rounded-full bg-gray-300" />
                              <span>
                                <AppFormattedMessage id="EVENT_ID" /> {mobileEventDisplayId}
                              </span>
                            </>
                          )}
                          {event.signatureSummary && (
                            <>
                              <span className="size-[3px] shrink-0 rounded-full bg-gray-300" />
                              <EventSignatureStatusCell
                                signatureSignedCount={event.signatureSummary.signatureSignedCount}
                                signatureStatus={event.signatureSummary.signatureStatus}
                                signatureTotalCount={event.signatureSummary.signatureTotalCount}
                              />
                            </>
                          )}
                        </span>
                      </div>
                      {event.type === EventTypeBackend.FUNDRAISING_ROUND && event.isOpen && (
                        <Tag
                          title={<AppFormattedMessage id="OPEN_ROUND" />}
                          variant={TagVariant.GREEN}
                        />
                      )}
                      {event.type === EventTypeBackend.FUNDRAISING_ROUND && !event.isOpen && (
                        <Tag
                          title={<AppFormattedMessage id="CLOSED_ROUND" />}
                          variant={TagVariant.RED}
                        />
                      )}
                    </div>
                    {shouldRenderPrePostValuation && (
                      <div className="mt-2 flex justify-between p-3">
                        <span className="text-xs font-[450] text-gray-700">
                          <AppFormattedMessage id="PRE_MONEY_VALUATION" />
                        </span>
                        <span className="text-sm font-[450] text-gray-700">
                          {preValuation.toLocaleString('en-US')}
                        </span>
                      </div>
                    )}
                    {shouldRenderPrePostValuation && (
                      <div className="mt-2 flex justify-between p-3">
                        <span className="text-xs font-[450] text-gray-700">
                          <AppFormattedMessage id="POST_MONEY_VALUATION" />
                        </span>
                        <span className="text-sm font-[450] text-gray-700">
                          {postMoneyValuation.toLocaleString('en-US')}
                        </span>
                      </div>
                    )}
                    <Button
                      className="flex items-center justify-between bg-brand-25 p-3"
                      onClick={() => navigate([event.eventId])}
                      styleType="NONE"
                    >
                      <span className="text-sm font-[450] text-brand-700">
                        <AppFormattedMessage
                          id="VIEW_TRANSACTIONS"
                          values={{ count: transactions.length || 1 }}
                        />
                      </span>
                      <ArrowIcon className="size-4 rotate-180" iconColor="#2565C8" />
                    </Button>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerFadeIn>
        ) : (
          <div className="centered flex">
            <EmptyEventHistory
              isDisplayedAddEvent={false}
              text={
                isSearchResultEmpty
                  ? 'No results match this search term Please try again'
                  : undefined
              }
              title={isSearchResultEmpty ? <AppFormattedMessage id="NO_EVENTS_FOUND" /> : undefined}
            />
          </div>
        )}
        <PaginationController
          className="shrink-0 max-lg:px-4 lg:mx-6"
          currentPage={currentPage}
          onClick={setCurrentPage}
          totalPages={totalPages}
        />
        <Sheet onOpenChange={toggler} open={isOpen}>
          <SheetContent className="w-full rounded-t-lg bg-white p-4" side="BOTTOM">
            <div className="flex h-[56px] w-full items-center justify-between">
              <span className="text-base font-semibold text-gray-700">
                <AppFormattedMessage id="FILTERS" />
              </span>
              <Button
                className="centered flex !size-7 h-fit w-fit rounded-full"
                onClick={handleCloseModal}
                style={{
                  boxShadow: '0px 0px 18.67px 0px #00000014',
                  border: '0.78px solid #F2F2F2',
                }}
                styleType="NONE"
              >
                <CloseIcon />
              </Button>
            </div>
            <div className="flex divide-x divide-gray-100">
              <div className="flex w-[144px] flex-col pr-4">
                <Button
                  className={twMerge(
                    'justify-start px-2 py-[6px] text-sm text-gray-700',
                    selectedFilter === 'ByType' && 'rounded bg-gray-50 font-[550]',
                  )}
                  onClick={() => setSelectedFilter('ByType')}
                  styleType="NONE"
                >
                  <AppFormattedMessage id="TYPES" />
                </Button>
                <Button
                  className={twMerge(
                    'justify-start px-2 py-[6px] text-sm text-gray-700',
                    selectedFilter === 'ByDate' && 'rounded bg-gray-50 font-[550]',
                  )}
                  onClick={() => setSelectedFilter('ByDate')}
                  styleType="NONE"
                >
                  <AppFormattedMessage id="DATE" />
                </Button>
              </div>
              <div className="flex w-full flex-col pl-4">
                {selectedFilter === 'ByType' ? (
                  <>
                    <div className="flex w-full items-center gap-2 rounded p-[6px] pr-8 text-sm text-gray-700">
                      <Checkbox
                        checked={isSelectedAllTypeFilters}
                        onChange={() => {
                          setTypeFilters(isSelectedAllTypeFilters ? [] : sortByTypeValues);
                        }}
                      />
                      <span className="text-sm font-normal text-gray-700">
                        <AppFormattedMessage id="ALL_TYPES" />
                      </span>
                    </div>
                    {sortByTypeMap.map(([sortKey]) => (
                      <div
                        className="flex w-full items-center gap-2 rounded p-[6px] pr-8 text-sm text-gray-700"
                        key={sortKey}
                      >
                        <Checkbox
                          checked={selectedTypeFilters.includes(sortKey)}
                          onChange={(state) =>
                            setTypeFilters((prev) =>
                              state
                                ? [...prev, sortKey]
                                : prev.filter((sortVariant) => sortVariant !== sortKey),
                            )
                          }
                        />
                        <span className="text-nowrap text-sm font-normal text-gray-700">
                          <AppFormattedMessage id={sortByTypeTitle[sortKey as SortByType]} />
                        </span>
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    {sortByDateMap.map(([sortKey, sortTitle]) => (
                      <div className="flex w-full items-center gap-2 rounded p-[6px] pr-8 text-sm text-gray-700">
                        <Checkbox
                          checked={sortKey === selectedDateFilter}
                          className="rounded-full"
                          onChange={() => setDateFilter(sortKey)}
                        />
                        <span className="text-sm font-normal text-gray-700">{sortTitle}</span>
                      </div>
                    ))}
                    {selectedDateFilter === SortByDate.CUSTOM_DATE && (
                      <div className="flex flex-col gap-[21px] border-t border-gray-100 pt-4">
                        <div className="flex flex-col gap-2">
                          <span className="text-label-sm font-medium text-gray-700">
                            <AppFormattedMessage id="START_DATE" />
                          </span>
                          <DatePicker
                            onSelect={(dateFrom) =>
                              setCustomDate((prev) => ({ ...prev, from: dateFrom }))
                            }
                            toDate={selectedCustomDate.to}
                            value={selectedCustomDate.from}
                          />
                        </div>
                        <div className="flex flex-col gap-2">
                          <span className="text-label-sm font-medium text-gray-700">
                            <AppFormattedMessage id="END_DATE" />
                          </span>
                          <DatePicker
                            fromDate={selectedCustomDate.from}
                            onSelect={(dateTo) =>
                              setCustomDate((prev) => ({ ...prev, to: dateTo }))
                            }
                            value={selectedCustomDate.to}
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="mt-6 flex w-full gap-4">
              <Button
                className="w-[137px] border border-gray-100 text-sm font-[450] text-gray-700"
                onClick={handleResetFilters}
                styleType="DEFAULT_ROUNDED"
              >
                <AppFormattedMessage id="RESET" />
              </Button>
              <Button
                className="w-full px-4 py-[10px] text-sm font-[550] text-white"
                onClick={handleApplyFilters}
              >
                <AppFormattedMessage id="APPLY_FILTER" />
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
};
